import { AnalyticsService } from '@alison/analytics/client';
import { InitialsAvatar } from '@alison/ui';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { initialsFrom } from '../../../../lib/format';
import { CreativePreview } from '../../../creatives/CreativePreview';
import { SectionSkeleton } from '../../../creatives/detail/shared/SectionSkeleton';
import { TagsPanel } from '../../../creatives/detail/tags/TagsPanel';
import { NetworkGlyph } from '../../../creatives/NetworkGlyph';
import { formatLiveDaysLabel } from '../../../creatives/utils';
import { keyList } from '../../../media/overview/MediaBoardSkeleton';
import { ErrorState } from '../../../media/shared/ErrorState';
import type { CompetitionCreativeData, CompetitionCreativeRow } from '../data/api';
import { useCompetitionCreative } from '../data/api';
import { flagEmoji, formatDateAdded } from '../utils';

const DRAWER_WIDTH = 850;
const PANEL_WIDTH = 304;
const PREVIEW_MAX_SIZE = 240;

type LoadedCreative = NonNullable<CompetitionCreativeData>;

interface CompetitionCreativeDrawerProps {
  creatives: CompetitionCreativeRow[];
  creativeId: string | null;
  accountIds: number[];
  onOpen: (creativeId: string) => void;
  onClose: () => void;
}

function MetaRow({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <Box>
      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{value}</Typography>
    </Box>
  );
}

function CreativeDetailPanelSkeleton(): ReactNode {
  return (
    <Stack sx={{ gap: 3, px: 4, py: 4, width: PANEL_WIDTH, flexShrink: 0 }}>
      <Skeleton
        variant="rounded"
        width={PREVIEW_MAX_SIZE}
        height={PREVIEW_MAX_SIZE}
        sx={{ mx: 'auto' }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Skeleton variant="circular" width={18} height={18} />
        <Skeleton variant="text" width="60%" />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Skeleton variant="rounded" width={28} height={28} />
        <Skeleton variant="text" width="50%" />
      </Box>
      <Stack sx={{ gap: 2 }}>
        {keyList(5, 'meta').map((key) => (
          <Box key={key}>
            <Skeleton variant="text" width="35%" height={16} />
            <Skeleton variant="text" width="55%" height={20} />
          </Box>
        ))}
      </Stack>
    </Stack>
  );
}

function TagsPanelSkeleton(): ReactNode {
  return (
    <Stack sx={{ gap: 5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Skeleton variant="rounded" width={90} height={32} />
        <Skeleton variant="rounded" width={90} height={32} />
        <Skeleton variant="text" width={120} sx={{ ml: 'auto' }} />
      </Box>
      <SectionSkeleton variant="table" />
    </Stack>
  );
}

function CompetitorDetailPanel({ data }: { data: LoadedCreative }): ReactNode {
  const initials = initialsFrom(data.competitorName);

  return (
    <Stack sx={{ gap: 3, px: 4, py: 4, width: PANEL_WIDTH, flexShrink: 0 }}>
      <CreativePreview
        name={data.creativeId}
        creativeType={data.mediaType}
        format={data.aspectRatio}
        duration={data.durationSeconds}
        thumbnailUrl={data.thumbnailUrl ?? ''}
        previewUrl={data.previewUrl ?? ''}
        maxSize={PREVIEW_MAX_SIZE}
        showCopyButton
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <NetworkGlyph network={data.network} />
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          {formatLiveDaysLabel(data.mediaType, data.daysRunning)}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {data.logoUrl === null && initials === null ? (
          <Box sx={{ width: 28, height: 28, flexShrink: 0 }} />
        ) : (
          <InitialsAvatar
            variant="rounded"
            size={28}
            initials={initials ?? ''}
            src={data.logoUrl ?? undefined}
          />
        )}
        <Typography sx={{ fontWeight: 700 }}>{data.competitorName ?? 'Unknown'}</Typography>
      </Box>
      <Stack sx={{ gap: 2 }}>
        <MetaRow
          label="Days running"
          value={data.daysRunning === null ? '—' : String(data.daysRunning)}
        />
        <MetaRow label="Date added" value={formatDateAdded(data.firstSeenDate)} />
        <MetaRow label="Network" value={data.network ?? '—'} />
        <MetaRow label="Tagging" value={data.tagged ? 'Tagged' : 'Elements not identified yet'} />
        <MetaRow
          label="Markets"
          value={
            data.markets.length === 0 ? '—' : [...data.markets].sort().map(flagEmoji).join(' ')
          }
        />
      </Stack>
    </Stack>
  );
}

export function CompetitionCreativeDrawer({
  creatives,
  creativeId,
  accountIds,
  onOpen,
  onClose,
}: CompetitionCreativeDrawerProps): ReactNode {
  const at = creatives.findIndex((creative) => creative.creativeId === creativeId);

  const query = useCompetitionCreative(
    { creativeId: creativeId ?? '', ...(accountIds.length ? { accountIds } : {}) },
    creativeId !== null,
  );

  if (creativeId === null) return null;

  function navigate(direction: 'previous' | 'next'): void {
    const target = creatives[direction === 'previous' ? at - 1 : at + 1];
    if (target === undefined) return;
    AnalyticsService.track('Competition Creative Detail - Navigated', { direction });
    onOpen(target.creativeId);
  }

  const data = query.data;

  return (
    <Drawer
      anchor="right"
      open
      onClose={onClose}
      slotProps={{ paper: { sx: { width: `min(${DRAWER_WIDTH}px, 100vw)` } } }}
      data-testid="competition-creative-drawer"
    >
      <Stack sx={{ height: '100%' }}>
        <Stack
          direction="row"
          sx={{
            gap: 2,
            px: 6,
            py: 2,
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <IconButton
            aria-label="Previous creative"
            disabled={at <= 0}
            onClick={() => navigate('previous')}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
            {at === -1 ? '—' : at + 1} / {creatives.length}
          </Typography>
          <IconButton
            aria-label="Next creative"
            disabled={at === -1 || at >= creatives.length - 1}
            onClick={() => navigate('next')}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
          <Stack sx={{ flex: 1 }} />
          <IconButton aria-label="Close" onClick={onClose}>
            <CloseOutlined fontSize="small" />
          </IconButton>
        </Stack>
        <Stack direction="row" sx={{ flex: 1, minHeight: 0 }}>
          {query.isError || data === null ? (
            <Box sx={{ flex: 1, py: 5, px: 4 }}>
              <ErrorState />
            </Box>
          ) : data === undefined ? (
            <>
              <CreativeDetailPanelSkeleton />
              <Box sx={{ flex: 1, overflowY: 'auto', py: 5, px: 4 }}>
                <TagsPanelSkeleton />
              </Box>
            </>
          ) : (
            <>
              <CompetitorDetailPanel data={data} />
              <Box sx={{ flex: 1, overflowY: 'auto', py: 5, px: 4 }}>
                {data.tagged ? (
                  <TagsPanel
                    categories={data.categories}
                    tagCount={data.tagCount}
                    durationSeconds={data.durationSeconds}
                    isVideo={data.mediaType?.toUpperCase() === 'VIDEO'}
                  />
                ) : (
                  <Typography sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                    Elements not identified yet.
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Stack>
      </Stack>
    </Drawer>
  );
}
